import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';

export default function ParrotConfigurationChat() {
    const navigate = useNavigate();
    const { getToken, isSignedIn, isLoaded } = useAuth();
    const [conversationId, setConversationId] = useState(null);
    const [messages, setMessages] = useState([]);
    const [currentMessage, setCurrentMessage] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isStarting, setIsStarting] = useState(true);
    const [voices, setVoices] = useState([]);
    const [selectedVoice, setSelectedVoice] = useState('');
    const [temperature, setTemperature] = useState(0.7);
    const [showCompletion, setShowCompletion] = useState(false);
    const [playingPreview, setPlayingPreview] = useState(null);

    // Authentication and subscription state
    const [authLoading, setAuthLoading] = useState(true);
    const [authError, setAuthError] = useState(null);
    const [userSubscription, setUserSubscription] = useState(null);

    const messagesEndRef = useRef(null);
    const inputRef = useRef(null);

    // Check authentication and subscription status
    useEffect(() => {
        const checkAuthAndSubscription = async () => {
            if (!isLoaded) return;

            if (!isSignedIn) {
                navigate('/sign-in');
                return;
            }

            try {
                const token = await getToken();
                const response = await fetch('https://parrotmate.com/api/auth/me', {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                });

                if (!response.ok) {
                    throw new Error('Failed to fetch user data');
                }

                const userData = await response.json();

                // Check if user has an active subscription
                if (!userData.subscription || userData.subscription.status !== 'active') {
                    navigate('/plans');
                    return;
                }

                setUserSubscription(userData.subscription);
                setAuthLoading(false);
            } catch (error) {
                console.error('Auth check failed:', error);
                setAuthError('Failed to verify subscription status');
                setAuthLoading(false);
            }
        };

        checkAuthAndSubscription();
    }, [isLoaded, isSignedIn, getToken, navigate]);

    // Fetch voices and start conversation
    useEffect(() => {
        if (!authLoading && userSubscription) {
            fetchVoices();
            startConversation();
        }
    }, [authLoading, userSubscription]);

    // Auto-scroll to bottom when new messages arrive
    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    const fetchVoices = async () => {
        try {
            const token = await getToken();
            const response = await fetch('https://parrotmate.com/api/voices', {
                headers: {
                    'Authorization': `Bearer ${token}`,
                },
            });

            if (response.ok) {
                const voicesData = await response.json();
                setVoices(voicesData);
                // Set first voice as default if none selected
                if (voicesData.length > 0 && !selectedVoice) {
                    setSelectedVoice(voicesData[0].service_voice_id);
                }
            }
        } catch (error) {
            console.error('Failed to fetch voices:', error);
        }
    };

    const startConversation = async () => {
        try {
            const token = await getToken();
            const response = await fetch('https://parrotmate.com/api/parrots/chat/start', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify({
                    conversation_type: 'parrot_setup'
                })
            });

            if (response.ok) {
                const data = await response.json();
                setConversationId(data.conversation_id);
                setMessages([{
                    role: 'assistant',
                    content: data.message,
                    timestamp: new Date()
                }]);
            } else {
                console.error('Failed to start conversation');
            }
        } catch (error) {
            console.error('Error starting conversation:', error);
        } finally {
            setIsStarting(false);
        }
    };

    const sendMessage = async () => {
        if (!currentMessage.trim() || !conversationId || isLoading) return;

        const userMessage = {
            role: 'user',
            content: currentMessage,
            timestamp: new Date()
        };

        setMessages(prev => [...prev, userMessage]);
        setCurrentMessage('');
        setIsLoading(true);

        try {
            const token = await getToken();
            const response = await fetch('https://parrotmate.com/api/parrots/chat/message', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify({
                    conversation_id: conversationId,
                    message: currentMessage
                })
            });

            if (response.ok) {
                const data = await response.json();
                const assistantMessage = {
                    role: 'assistant',
                    content: data.message,
                    timestamp: new Date()
                };
                setMessages(prev => [...prev, assistantMessage]);

                // Check if the conversation seems ready for completion
                if (data.message.toLowerCase().includes('ready') ||
                    data.message.toLowerCase().includes('complete') ||
                    data.message.toLowerCase().includes('finalize')) {
                    setShowCompletion(true);
                }
            } else {
                console.error('Failed to send message');
            }
        } catch (error) {
            console.error('Error sending message:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const completeConfiguration = async () => {
        if (!conversationId || !selectedVoice) return;

        setIsLoading(true);
        try {
            const token = await getToken();
            const response = await fetch('https://parrotmate.com/api/parrots/chat/complete', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify({
                    conversation_id: conversationId,
                    voice_id: selectedVoice,
                    temperature: temperature
                })
            });

            if (response.ok) {
                const data = await response.json();
                // Show success message with the first message
                setMessages(prev => [...prev, {
                    role: 'system',
                    content: `🎉 Your parrot has been created successfully!\n\nFirst Message: "${data.first_message}"\n\nYour parrot is now ready to handle calls. You can view and manage it from your dashboard.`,
                    timestamp: new Date()
                }]);

                // Redirect to dashboard after a delay
                setTimeout(() => {
                    navigate('/dashboard');
                }, 3000);
            } else {
                console.error('Failed to complete configuration');
            }
        } catch (error) {
            console.error('Error completing configuration:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const playVoicePreview = async (voiceId, previewFile) => {
        if (playingPreview === voiceId) {
            setPlayingPreview(null);
            return;
        }

        try {
            setPlayingPreview(voiceId);
            const audio = new Audio(`https://parrotmate.com/api/voices/preview/${previewFile}`);
            audio.onended = () => setPlayingPreview(null);
            audio.onerror = () => setPlayingPreview(null);
            await audio.play();
        } catch (error) {
            console.error('Failed to play preview:', error);
            setPlayingPreview(null);
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            sendMessage();
        }
    };

    // Show loading screen while checking authentication
    if (!isLoaded || authLoading || isStarting) {
        return (
            <div className="min-h-screen bg-gray-900 flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-500 mx-auto mb-4"></div>
                    <p className="text-gray-300 dark:text-gray-600">
                        {isStarting ? 'Starting your parrot configuration...' : 'Loading...'}
                    </p>
                </div>
            </div>
        );
    }

    // Show error if authentication failed
    if (authError) {
        return (
            <div className="min-h-screen bg-gray-900 flex items-center justify-center">
                <div className="text-center">
                    <p className="text-red-400 mb-4">{authError}</p>
                    <button
                        onClick={() => navigate('/plans')}
                        className="px-6 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 dark:text-white"
                    >
                        Go to Plans
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-900 text-gray-200">
            <div className="container mx-auto p-4 max-w-4xl">
                <div className="mb-6">
                    <h1 className="text-3xl font-bold mb-2">Configure Your AI Parrot</h1>
                    <p className="text-gray-400">
                        Let's have a conversation to build the perfect voice parrot for your business.
                    </p>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Chat Interface */}
                    <div className="lg:col-span-2">
                        <div className="bg-gray-800 rounded-lg shadow-lg h-96 flex flex-col">
                            {/* Messages */}
                            <div className="flex-1 p-4 overflow-y-auto">
                                {messages.map((message, index) => (
                                    <div
                                        key={index}
                                        className={`mb-4 ${message.role === 'user' ? 'text-right' : 'text-left'
                                            }`}
                                    >
                                        <div
                                            className={`inline-block max-w-xs lg:max-w-md px-4 py-2 rounded-lg ${message.role === 'user'
                                                    ? 'bg-teal-600 text-white'
                                                    : message.role === 'system'
                                                        ? 'bg-green-600 text-white'
                                                        : 'bg-gray-700 text-gray-200'
                                                }`}
                                        >
                                            <p className="whitespace-pre-wrap">{message.content}</p>
                                            <p className="text-xs opacity-70 mt-1">
                                                {message.timestamp.toLocaleTimeString()}
                                            </p>
                                        </div>
                                    </div>
                                ))}
                                {isLoading && (
                                    <div className="text-left mb-4">
                                        <div className="inline-block bg-gray-700 text-gray-200 px-4 py-2 rounded-lg">
                                            <div className="flex items-center space-x-2">
                                                <div className="animate-pulse">Thinking...</div>
                                                <div className="flex space-x-1">
                                                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                                                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                                                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                                <div ref={messagesEndRef} />
                            </div>

                            {/* Input */}
                            <div className="p-4 border-t border-gray-700">
                                <div className="flex space-x-2">
                                    <textarea
                                        ref={inputRef}
                                        value={currentMessage}
                                        onChange={(e) => setCurrentMessage(e.target.value)}
                                        onKeyPress={handleKeyPress}
                                        placeholder="Type your message..."
                                        className="flex-1 bg-gray-700 text-white rounded-lg px-3 py-2 resize-none focus:outline-none focus:ring-2 focus:ring-teal-500 dark:text-white"
                                        rows="2"
                                        disabled={isLoading}
                                    />
                                    <button
                                        onClick={sendMessage}
                                        disabled={isLoading || !currentMessage.trim()}
                                        className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed dark:text-white"
                                    >
                                        Send
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Configuration Panel */}
                    <div className="space-y-6">
                        {/* Voice Selection */}
                        <div className="bg-gray-800 p-4 rounded-lg shadow-lg">
                            <h3 className="text-lg font-semibold text-white mb-3 dark:text-white">Voice Selection</h3>
                            <div className="space-y-2 max-h-64 overflow-y-auto">
                                {voices.map((voice) => (
                                    <div
                                        key={voice.service_voice_id}
                                        className={`p-3 rounded-lg border cursor-pointer transition-all ${selectedVoice === voice.service_voice_id
                                                ? 'border-teal-500 bg-teal-900/20'
                                                : 'border-gray-600 bg-gray-700 hover:border-gray-500'
                                            }`}
                                        onClick={() => setSelectedVoice(voice.service_voice_id)}
                                    >
                                        <div className="flex justify-between items-start">
                                            <div className="flex-1">
                                                <h4 className="font-medium text-white text-sm dark:text-white">{voice.parrotmate_name}</h4>
                                                <span className={`inline-block px-2 py-1 rounded text-xs font-medium ${voice.gender === 'Male' ? 'bg-blue-900 text-blue-200' : 'bg-pink-900 text-pink-200'
                                                    }`}>
                                                    {voice.gender}
                                                </span>
                                            </div>
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    playVoicePreview(voice.service_voice_id, voice.preview_audio_file);
                                                }}
                                                className={`p-1 rounded-full transition-colors ${playingPreview === voice.service_voice_id
                                                        ? 'bg-red-600 hover:bg-red-700'
                                                        : 'bg-teal-600 hover:bg-teal-700'
                                                    }`}
                                                title={playingPreview === voice.service_voice_id ? 'Stop Preview' : 'Play Preview'}
                                            >
                                                {playingPreview === voice.service_voice_id ? (
                                                    <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zM7 8a1 1 0 012 0v4a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v4a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                                                    </svg>
                                                ) : (
                                                    <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
                                                    </svg>
                                                )}
                                            </button>
                                        </div>
                                        <p className="text-xs text-gray-400 mt-1">{voice.short_description}</p>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Temperature */}
                        <div className="bg-gray-800 p-4 rounded-lg shadow-lg">
                            <h3 className="text-lg font-semibold text-white mb-3 dark:text-white">Creativity Level</h3>
                            <div className="space-y-2">
                                <button
                                    onClick={() => setTemperature(0.1)}
                                    className={`w-full p-2 rounded text-sm transition ${temperature === 0.1 ? 'bg-teal-600 text-white' : 'bg-gray-700 hover:bg-gray-600'
                                        }`}
                                >
                                    Deterministic
                                </button>
                                <button
                                    onClick={() => setTemperature(0.7)}
                                    className={`w-full p-2 rounded text-sm transition ${temperature === 0.7 ? 'bg-teal-600 text-white' : 'bg-gray-700 hover:bg-gray-600'
                                        }`}
                                >
                                    Creative
                                </button>
                                <button
                                    onClick={() => setTemperature(1.0)}
                                    className={`w-full p-2 rounded text-sm transition ${temperature === 1.0 ? 'bg-teal-600 text-white' : 'bg-gray-700 hover:bg-gray-600'
                                        }`}
                                >
                                    More Creative
                                </button>
                            </div>
                        </div>

                        {/* Complete Configuration */}
                        {showCompletion && (
                            <div className="bg-gray-800 p-4 rounded-lg shadow-lg">
                                <h3 className="text-lg font-semibold text-white mb-3 dark:text-white">Ready to Complete?</h3>
                                <p className="text-sm text-gray-400 mb-4">
                                    Once you're satisfied with our conversation, click below to create your parrot.
                                </p>
                                <button
                                    onClick={completeConfiguration}
                                    disabled={isLoading || !selectedVoice}
                                    className="w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed dark:text-white"
                                >
                                    {isLoading ? 'Creating Parrot...' : 'Complete Configuration'}
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
